// Shared helpers for displaying device backhaul latency.
//
// The backend exposes a per-device `latency` block on /devices and
// /:serial/device responses:
//
//   latency: {
//     to_parent: { median_ms, avg_ms, min_ms, max_ms, jitter_ms, samples, target },
//     to_cap:    { ... }
//   }
//
// Median is the headline number (typical RTT), jitter (max-min) is a
// secondary stability hint. CAP devices only carry `to_parent`.

// Latency thresholds in milliseconds (median round-trip). These are the
// same defaults used throughout the UI so chips, colors and alert
// thresholds stay consistent.
export const LATENCY_GOOD_MS = 10;
export const LATENCY_FAIR_MS = 30;
export const LATENCY_POOR_MS = 50;

// Default alerts threshold for "high parent latency" (ms).
export const DEFAULT_PARENT_LATENCY_ALERT_MS = 30;

// Pull a numeric ms value from a latency summary object, defaulting to median.
// Returns null when the value is missing/non-numeric so callers can show '-'.
export const getLatencyValue = (summary, field = 'median_ms') => {
  if (!summary) return null;
  const v = summary[field];
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

// Convenience accessors.
export const getParentLatencyMs = (device) =>
  getLatencyValue(device?.latency?.to_parent);
export const getCapLatencyMs = (device) =>
  getLatencyValue(device?.latency?.to_cap);

// Returns the worst (highest) of the per-target medians, or null if none.
// Used by aggregate views that need a single "device latency" number.
export const getWorstLatencyMs = (device) => {
  const p = getParentLatencyMs(device);
  const c = getCapLatencyMs(device);
  if (p === null && c === null) return null;
  if (p === null) return c;
  if (c === null) return p;
  return Math.max(p, c);
};

// MUI Chip color name based on ms thresholds. Lower = better.
//   <= 10 ms      success
//   <= 30 ms      success (still "Good")
//   <= 50 ms      warning ("Fair")
//   >  50 ms      error   ("Poor")
export const getLatencyChipColor = (ms) => {
  if (ms === null || ms === undefined) return 'default';
  if (ms <= LATENCY_FAIR_MS) return 'success';
  if (ms <= LATENCY_POOR_MS) return 'warning';
  return 'error';
};

// Raw hex color (for inline sx={{ color }}). Mirrors the chip mapping
// using common Material palette accents so both styles look consistent.
export const getLatencyHexColor = (ms) => {
  if (ms === null || ms === undefined) return '#9AA7BD';
  if (ms <= LATENCY_FAIR_MS) return '#2E7D32'; // green 800
  if (ms <= LATENCY_POOR_MS) return '#78909c';
  return '#D32F2F'; // red 700
};

// Short qualitative label.
export const getLatencyLabel = (ms) => {
  if (ms === null || ms === undefined) return 'N/A';
  if (ms <= LATENCY_GOOD_MS) return 'Excellent';
  if (ms <= LATENCY_FAIR_MS) return 'Good';
  if (ms <= LATENCY_POOR_MS) return 'Fair';
  return 'Poor';
};

// Format a latency value as e.g. "4.2 ms" / "12 ms". Returns the
// `fallback` (default '—') when ms is missing.
export const formatLatencyMs = (ms, fallback = '—') => {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return fallback;
  // Sub-10ms gets one decimal for precision; larger values are fine as ints.
  if (ms < 10) return `${ms.toFixed(1)} ms`;
  return `${Math.round(ms)} ms`;
};

// Map a millisecond round-trip value into a 0–100 quality score where
// higher = better. Used by aggregate backhaul-health calculations that
// previously consumed the legacy 0–1 latency_quality field.
//
//   <= 10 ms       → 100 (excellent)
//   10–30 ms       → 100 → 80 (linear)
//   30–50 ms       → 80 → 60
//   50–100 ms      → 60 → 30
//   > 100 ms       → 30 → 0 (asymptotic)
export const latencyMsToScore = (ms) => {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return null;
  if (ms <= 10) return 100;
  if (ms <= 30) return 100 - ((ms - 10) / 20) * 20;       // 100 → 80
  if (ms <= 50) return 80 - ((ms - 30) / 20) * 20;        // 80 → 60
  if (ms <= 100) return 60 - ((ms - 50) / 50) * 30;       // 60 → 30
  // For very high latency, decay smoothly toward 0.
  return Math.max(0, 30 * (100 / Math.max(100, ms)));
};

// Build a multi-line tooltip describing a single LatencySummary.
//
// The `label` argument is expected to be a customer-friendly description of
// what was probed (e.g. "Median to parent target" / "Median to CAP target").
// We render it inline with the headline median value so the tooltip reads
// naturally:
//
//   Median to CAP target: 12 ms
//   Avg: 14 ms
//   Min: 9 ms
//   Max: 22 ms
//   Jitter: 13 ms
//   Samples: 30
//
// We intentionally do NOT include the raw `summary.target` hostname/IP — the
// underlying address is a backend implementation detail.
export const formatLatencyTooltip = (summary, label) => {
  if (!summary) return label ? `${label}: no data` : 'no data';
  const median = formatLatencyMs(summary.median_ms);
  const parts = [];
  parts.push(label ? `${label}: ${median}` : `Median: ${median}`);
  parts.push(`Avg: ${formatLatencyMs(summary.avg_ms)}`);
  parts.push(`Min: ${formatLatencyMs(summary.min_ms)}`);
  parts.push(`Max: ${formatLatencyMs(summary.max_ms)}`);
  parts.push(`Jitter: ${formatLatencyMs(summary.jitter_ms)}`);
  if (typeof summary.samples === 'number') {
    parts.push(`Samples: ${summary.samples}`);
  }
  return parts.join('\n');
};
